// ManualEmitModal — Story 10.4 (E10).
// Modal pra emissão manual de NFS-e em cobrança paga.
// Suporta override de documento + serviceDescription + amount + emissionDate +
// savePermanent (checkbox condicional quando documento foi alterado).

import * as Dialog from '@radix-ui/react-dialog';
import { FileText, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  formatDocument,
  isValidCpfOrCnpj,
  normalizeDocument,
  todayLocal,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { useEmitManualFiscalInvoice } from '../../hooks/use-fiscal';
import { extractErrorMessage } from '../../lib/errors';
import type { NbsCodeOption } from '../../lib/fiscal-api';
import { Field, Input } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';

import { NbsPicker } from './NbsPicker';

export interface ManualEmitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chargeId: string;
  chargeAmount: string;
  /** documento pré-preenchido (fiscal_document_override || client.document) */
  defaultTakerDocument: string | null;
  /** documento principal do cliente — pra comparar e decidir mostrar savePermanent */
  clientPrincipalDocument: string | null;
  /** descrição pré-preenchida (plan.service_description) — null se charge avulsa */
  defaultServiceDescription: string | null;
  onSuccess?: () => void;
}

export function ManualEmitModal({
  open,
  onOpenChange,
  chargeId,
  chargeAmount,
  defaultTakerDocument,
  clientPrincipalDocument,
  defaultServiceDescription,
  onSuccess,
}: ManualEmitModalProps): JSX.Element {
  const [takerDocument, setTakerDocument] = useState(defaultTakerDocument ?? '');
  const [serviceDescription, setServiceDescription] = useState(defaultServiceDescription ?? '');
  const [amount, setAmount] = useState(chargeAmount);
  const [emissionDate, setEmissionDate] = useState(todayLocal());
  const [savePermanent, setSavePermanent] = useState(false);
  const [nbs, setNbs] = useState<NbsCodeOption | null>(null);

  const emit = useEmitManualFiscalInvoice();

  // Reset ao reabrir
  useEffect(() => {
    if (open) {
      setTakerDocument(defaultTakerDocument ?? '');
      setServiceDescription(defaultServiceDescription ?? '');
      setAmount(chargeAmount);
      setEmissionDate(todayLocal());
      setSavePermanent(false);
      setNbs(null);
    }
  }, [open, defaultTakerDocument, defaultServiceDescription, chargeAmount]);

  function handleClose(): void {
    onOpenChange(false);
  }

  const normalizedDoc = normalizeDocument(takerDocument);
  const normalizedPrincipal = clientPrincipalDocument
    ? normalizeDocument(clientPrincipalDocument)
    : '';
  const docChanged = normalizedDoc !== normalizedPrincipal && normalizedDoc.length > 0;

  const docValid = normalizedDoc.length === 0 || isValidCpfOrCnpj(normalizedDoc);
  const descValid =
    serviceDescription.trim().length >= 10 && serviceDescription.trim().length <= 500;
  const amountNum = Number.parseFloat(amount);
  const chargeAmountNum = Number.parseFloat(chargeAmount);
  const amountValid = Number.isFinite(amountNum) && amountNum > 0 && amountNum <= chargeAmountNum;
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(emissionDate);

  const canSubmit = docValid && descValid && amountValid && dateValid && !emit.isPending;

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return;
    try {
      const result = await emit.mutateAsync({
        charge_id: chargeId,
        ...(normalizedDoc.length > 0 ? { taker_document: normalizedDoc } : {}),
        service_description: serviceDescription.trim(),
        amount,
        emission_date: emissionDate,
        save_permanent: docChanged && savePermanent,
        ...(nbs ? { nbs_code: nbs.code } : {}),
      });
      if (result.status === 'issued') {
        toast.success('NF emitida com sucesso.', {
          description: result.pdfUrl ? 'PDF já disponível.' : undefined,
        });
      } else {
        toast.success('NF criada no Asaas — aguardando autorização da prefeitura.', {
          description: 'O status atualiza aqui sozinho quando a prefeitura confirmar.',
        });
      }
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error('Não conseguimos emitir.', {
        description: extractErrorMessage(err, 'Tente de novo.'),
      });
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(640px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="flex items-center gap-2 text-[16px] font-semibold tracking-[-0.005em] text-ink">
              <FileText size={16} strokeWidth={2} />
              Emitir NF manual
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Description className="mb-4 text-[12px] text-muted-2">
            Os valores vêm pré-preenchidos do plano e do cliente. Você pode ajustar antes de emitir.
          </Dialog.Description>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label="CPF/CNPJ do tomador"
              hint={
                !docValid
                  ? undefined
                  : docChanged
                    ? `Diferente do principal ${formatDocument(normalizedPrincipal)}`
                    : 'Documento principal do cliente'
              }
              error={!docValid ? 'CPF/CNPJ inválido' : undefined}
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="text"
                  value={takerDocument}
                  onChange={(e) => setTakerDocument(e.target.value)}
                  placeholder="00.000.000/0000-00"
                  hasError={!docValid}
                  tabular
                />
              )}
            </Field>

            <Field
              label="Valor"
              hint={`Cobrança original: R$ ${chargeAmount}`}
              error={
                !amountValid && amount
                  ? amountNum > chargeAmountNum
                    ? `Não pode exceder R$ ${chargeAmount}`
                    : 'Valor inválido'
                  : undefined
              }
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                  placeholder="100.00"
                  hasError={!amountValid}
                  tabular
                />
              )}
            </Field>

            <Field
              label="Data de emissão"
              hint="Hoje ou até 30 dias retroativo"
              error={!dateValid ? 'Formato YYYY-MM-DD' : undefined}
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="date"
                  value={emissionDate}
                  max={todayLocal()}
                  onChange={(e) => setEmissionDate(e.target.value)}
                  hasError={!dateValid}
                  tabular
                />
              )}
            </Field>

            <div />

            <Field
              label="Descrição do serviço"
              hint={`${serviceDescription.length}/500 — mínimo 10`}
              error={
                serviceDescription.length > 0 && !descValid
                  ? serviceDescription.length < 10
                    ? `Mínimo 10 caracteres (${serviceDescription.length}/10)`
                    : 'Máximo 500 caracteres'
                  : undefined
              }
              className="md:col-span-2"
            >
              {({ inputId }) => (
                <textarea
                  id={inputId}
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Ex: Mensalidade do plano OwlTask Pro - Plataforma SaaS"
                  className={cn(
                    'w-full rounded-[10px] border bg-panel px-3 py-2 text-[13px] text-ink',
                    'placeholder:text-muted-3 outline-none transition-colors',
                    'focus:border-line-2 resize-y',
                    serviceDescription.length > 0 && !descValid ? 'border-bad/70' : 'border-line',
                  )}
                />
              )}
            </Field>

            <Field
              label="Código NBS"
              hint="Opcional. Nomenclatura Brasileira de Serviços — escolha da lista oficial."
              className="md:col-span-2"
            >
              {({ inputId, describedBy }) => (
                <NbsPicker id={inputId} describedBy={describedBy} value={nbs} onChange={setNbs} />
              )}
            </Field>

            {docChanged && (
              <label className="flex cursor-pointer items-start gap-2 rounded-[10px] border border-line bg-panel-2 p-3 md:col-span-2">
                <input
                  type="checkbox"
                  checked={savePermanent}
                  onChange={(e) => setSavePermanent(e.target.checked)}
                  className="mt-[3px]"
                />
                <span className="text-[12px] text-muted">
                  <strong className="text-ink">Salvar como padrão</strong> pra próximas NFs deste
                  cliente — atualiza{' '}
                  <code className="font-mono text-[11px] text-muted-2">
                    fiscal_document_override
                  </code>{' '}
                  no perfil dele.
                </span>
              </label>
            )}
          </div>

          <div className="mt-6 flex items-center justify-end gap-2 border-t border-line/40 pt-4">
            <PillButton type="button" onClick={handleClose} disabled={emit.isPending}>
              Cancelar
            </PillButton>
            <PillButton
              type="button"
              solid
              onClick={() => void handleSubmit()}
              disabled={!canSubmit}
            >
              {emit.isPending ? 'Emitindo…' : 'Emitir NF'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
